import { useEffect, useMemo, useState } from 'react';
import { ChevronDown, PlayCircle, Dumbbell } from 'lucide-react';
import { api } from '@/lib/api';

/**
 * Exercise library — the distinct exercises in the client's assigned programs,
 * grouped by muscle group. Each card expands to play the YouTube demo inline and
 * show the coach's notes. Exercises without a video just don't show a player;
 * those without notes just don't show a notes block.
 *
 *   GET /api/workouts/my-exercises
 */

interface LibExercise {
  id: string;
  name: string;
  category: string | null;
  muscle_group: string | null;
  equipment: string | null;
  video_url: string | null;
  coaching_notes: string | null;
}

/** watch?v=ID / youtu.be/ID / shorts/ID -> embed URL, or null if unparseable. */
function youtubeEmbed(url: string): string | null {
  try {
    const u = new URL(url);
    let id = '';
    if (u.hostname.includes('youtu.be')) id = u.pathname.slice(1);
    else if (u.pathname.startsWith('/shorts/')) id = u.pathname.split('/')[2] ?? '';
    else id = u.searchParams.get('v') ?? '';
    id = id.split(/[?&]/)[0];
    return id ? `https://www.youtube.com/embed/${id}` : null;
  } catch {
    return null;
  }
}

function titleCase(s: string): string {
  return s.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

export function ExerciseLibrary() {
  const [exercises, setExercises] = useState<LibExercise[]>([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState<string | null>(null);

  useEffect(() => {
    api
      .get<{ exercises: LibExercise[] }>('/api/workouts/my-exercises')
      .then((d) => setExercises(d.exercises || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  // Group by muscle group, preserving the muscle-then-name order from the API.
  const groups = useMemo(() => {
    const map = new Map<string, LibExercise[]>();
    for (const ex of exercises) {
      const key = ex.muscle_group ? titleCase(ex.muscle_group) : 'Other';
      (map.get(key) ?? map.set(key, []).get(key)!).push(ex);
    }
    return Array.from(map.entries());
  }, [exercises]);

  if (loading) {
    return (
      <div className="flex flex-col gap-3">
        {[1, 2, 3].map((i) => (
          <div key={i} className="card animate-pulse h-16" style={{ background: 'var(--surface)' }} />
        ))}
      </div>
    );
  }

  if (exercises.length === 0) {
    return (
      <div className="card text-center py-8">
        <Dumbbell size={28} className="mx-auto mb-2" style={{ color: 'var(--gold-muted)' }} />
        <p className="text-white font-semibold">No exercises yet</p>
        <p className="text-sm mt-1" style={{ color: 'var(--gray-400)' }}>
          Exercises from your assigned programs will appear here, with demo videos and coaching notes.
        </p>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-5">
      {groups.map(([muscle, items]) => (
        <div key={muscle}>
          <p className="text-xs font-semibold uppercase tracking-wider mb-2" style={{ color: 'var(--gold-muted)' }}>
            {muscle}
          </p>
          <div className="flex flex-col gap-2">
            {items.map((ex) => {
              const isOpen = open === ex.id;
              const embed = ex.video_url ? youtubeEmbed(ex.video_url) : null;
              const hasDetail = !!embed || !!ex.coaching_notes;
              return (
                <div key={ex.id} className="card" style={{ padding: 0, overflow: 'hidden' }}>
                  <button
                    onClick={() => setOpen(isOpen ? null : ex.id)}
                    className="w-full flex items-center justify-between gap-3 text-left px-4 py-3"
                    aria-expanded={isOpen}
                  >
                    <div className="min-w-0">
                      <p className="text-white font-medium truncate">{ex.name}</p>
                      <p className="text-xs truncate" style={{ color: 'var(--gray-400)' }}>
                        {[ex.equipment, ex.video_url ? 'Demo video' : null].filter(Boolean).join(' · ') || (ex.category ? titleCase(ex.category) : '')}
                      </p>
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      {ex.video_url && <PlayCircle size={16} style={{ color: 'var(--gold)' }} />}
                      {hasDetail && (
                        <ChevronDown
                          size={18}
                          style={{ color: 'var(--gray-500)', transform: isOpen ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s' }}
                        />
                      )}
                    </div>
                  </button>

                  {isOpen && hasDetail && (
                    <div className="px-4 pb-4 flex flex-col gap-3">
                      {embed && (
                        <div style={{ position: 'relative', width: '100%', paddingBottom: '56.25%', borderRadius: 8, overflow: 'hidden', background: '#000' }}>
                          <iframe
                            src={embed}
                            title={`${ex.name} demo`}
                            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                            allowFullScreen
                            style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', border: 'none' }}
                          />
                        </div>
                      )}
                      {ex.coaching_notes && (
                        <div>
                          <p className="text-xs font-semibold uppercase tracking-wider mb-1" style={{ color: 'var(--gold-muted)' }}>
                            Coaching notes
                          </p>
                          <p className="text-sm whitespace-pre-wrap" style={{ color: 'var(--gray-300)' }}>
                            {ex.coaching_notes}
                          </p>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
}
